import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import TvIcon from '@mui/icons-material/Tv';
import {
  DragIndicator,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Lock,
} from '@mui/icons-material';
import { SamsungDevice, samsungControl } from '../../services/samsungApi';
import DeviceTypeImage from '../shared/DeviceTypeImage';
import PowerButton from '../shared/PowerButton';
import { DeviceIconWithBrandChip } from '../shared/DeviceBrandChip';
import { artifactTypeLineSx, sonosCardOuterSx } from '../../installed/music';
import { useAiDevicePolicy, useShowAiCardBadge } from '../../contexts/AiDevicePolicyContext';
import { displayHvacMode } from './samsungModeUtils';
import { requirePinIfNeeded } from '../../services/pinGate';

const ACCENT = '#2391FF';
const CARD_BG = '#2F3235';
const PRESS = 'transform 120ms ease, background-color 140ms ease, border-color 140ms ease, color 140ms ease';
const SETPOINT_DEBOUNCE_MS = 700;
const SETPOINT_MIN = 18;
const SETPOINT_MAX = 30;

const HVAC_TINT: Record<string, string> = {
  auto: '#90A4AE',
  cool: '#42A5F5',
  heat: '#FF8A65',
  fan: '#81C784',
  dry: '#4DD0E1',
  wind: '#81C784',
};

/** Slot izquierdo mute TV: ancho fijo para alinear el chip de volumen. */
const LEFT_SLOT_SX = {
  flexShrink: 0,
  width: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  lineHeight: 1,
} as const;

/** Contenedor chip (ambiente AC / − valor +) — compacto para caber en tarjeta 64px. */
const VALUE_CHIP_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 0.35,
  py: 0,
  borderRadius: '8px',
  bgcolor: 'rgba(0,0,0,0.28)',
  border: '1px solid rgba(255,255,255,0.08)',
  flexShrink: 0,
  height: 22,
  boxSizing: 'border-box',
} as const;

/** Chip − / valor / + (setpoint AC y volumen TV): idéntico. */
const STEP_CHIP_SX = {
  ...VALUE_CHIP_SX,
  gap: 0.2,
  ml: 1.25,
  justifyContent: 'flex-start',
} as const;

const STEP_BTN_SX = {
  p: 0.15,
  color: '#fff',
  borderRadius: '6px',
  transition: PRESS,
  WebkitTapHighlightColor: 'transparent',
  '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(255,255,255,0.08)' },
  '&:active:not(.Mui-disabled)': { transform: 'scale(0.88)' },
  '&.Mui-disabled': { color: '#666' },
} as const;

const STEP_VALUE_SX = {
  minWidth: 26,
  textAlign: 'center' as const,
  fontSize: 13,
  fontWeight: 750,
  color: '#fff',
  letterSpacing: '-0.02em',
  lineHeight: 1,
};

interface Props {
  device: SamsungDevice;
  onOpenModal?: (device: SamsungDevice) => void;
  onUpdated?: (d: SamsungDevice) => void;
  interactionLocked?: boolean;
  /** Settings lápiz: como Shelly `isEditing` — oculta power (queda el checkbox rojo). */
  editMode?: boolean;
  isRoomEditing?: boolean;
  isEditingName?: boolean;
  dragHandleProps?: Record<string, unknown>;
  onStartEditName?: () => void;
  onSaveEditName?: (newName: string) => void;
  onCancelEditName?: () => void;
  /** Chip de marca sobre el ícono (solo Settings modo edición). */
  showBrandChip?: boolean;
}

const SamsungDeviceCard: React.FC<Props> = ({
  device,
  onOpenModal,
  onUpdated,
  interactionLocked = false,
  editMode = false,
  isRoomEditing = false,
  isEditingName = false,
  dragHandleProps,
  onStartEditName,
  onSaveEditName,
  onCancelEditName,
  showBrandChip = false,
}) => {
  const { t } = useTranslation();
  const showAiCardBadge = useShowAiCardBadge();
  const aiPolicy = useAiDevicePolicy();
  const aiWhitelisted = showAiCardBadge && aiPolicy.isSamsungWhitelistedForIcon(device.id);
  const aiBlacklisted = showAiCardBadge && aiPolicy.isSamsungBlacklistedForIcon(device.id);
  const locked = interactionLocked || isRoomEditing || isEditingName;
  // En modo edición Settings no hay power ni ± (paridad Shelly).
  const canControl = Boolean(device.can_control) && !locked && !editMode;
  const showPower = !editMode && !locked && !isRoomEditing;
  const offline = !device.online;
  const health = String(device.health_status || '').toUpperCase();
  const tvCloudSleepy = device.device_kind === 'tv'
    && device.control_mode !== 'lan'
    && !offline
    && ['OFFLINE', 'UNHEALTHY', 'INACTIVE'].includes(health);
  const hasArtefactoIcon = Boolean(device.artefacto?.nombre || device.artefacto_id);
  const artefactoNombre = device.artefacto?.nombre || null;
  const tipoArtefacto = artefactoNombre
    ?? (device.device_kind === 'ac' ? t('samsung.card.fallbackAc') : t('samsung.card.fallbackTv'));
  const [powerBusy, setPowerBusy] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState(device.nombre);
  const [localSetpoint, setLocalSetpoint] = useState<number | null>(null);
  const setpointDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSetpointRef = useRef<number | null>(null);
  const setpointSeqRef = useRef(0);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;
  const deviceIdRef = useRef(device.id);
  deviceIdRef.current = device.id;

  const applyUpdate = (d: SamsungDevice) => {
    onUpdated?.(d);
  };

  const flushSetpoint = useCallback(async (next: number) => {
    const seq = ++setpointSeqRef.current;
    try {
      const res = await samsungControl(deviceIdRef.current, 'setpoint', next);
      if (seq !== setpointSeqRef.current) return;
      if (res?.device) onUpdatedRef.current?.(res.device);
    } catch {
      if (seq !== setpointSeqRef.current) return;
      setLocalSetpoint(null);
    }
  }, []);

  useEffect(() => {
    if (localSetpoint == null) return;
    if (pendingSetpointRef.current != null) return;
    if (device.setpoint != null && Math.round(Number(device.setpoint)) === Math.round(localSetpoint)) {
      setLocalSetpoint(null);
    }
  }, [device.setpoint, localSetpoint]);

  useEffect(() => () => {
    if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
  }, []);

  useEffect(() => {
    if (isEditingName) setEditingNameValue(device.nombre);
  }, [isEditingName, device.id, device.nombre]);

  const executeControl = async (action: string, value?: unknown) => {
    if (!canControl) return;
    // TV: no bloquear por online — ST reporta health OFFLINE con status cacheado.
    if (offline && device.device_kind === 'ac') return;
    if (action === 'setpoint') {
      const next = Math.min(SETPOINT_MAX, Math.max(SETPOINT_MIN, Number(value)));
      if (!Number.isFinite(next)) return;
      setLocalSetpoint(next);
      pendingSetpointRef.current = next;
      if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
      setpointDebounceRef.current = setTimeout(() => {
        setpointDebounceRef.current = null;
        const val = pendingSetpointRef.current;
        pendingSetpointRef.current = null;
        if (val != null) void flushSetpoint(val);
      }, SETPOINT_DEBOUNCE_MS);
      return;
    }
    try {
      const res = await samsungControl(device.id, action, value);
      if (res?.device) applyUpdate(res.device);
    } catch { /* upstream */ }
  };

  const run = async (e: React.MouseEvent, action: string, value?: unknown) => {
    e.stopPropagation();
    e.preventDefault();
    if (!requirePinIfNeeded(device.pin_required, device.id, () => {
      void executeControl(action, value);
    })) return;
    await executeControl(action, value);
  };

  const doTogglePower = async () => {
    if (!canControl) return;
    if (offline && device.device_kind === 'ac') return;
    setPowerBusy(true);
    try {
      const res = await samsungControl(device.id, 'power', !device.power);
      if (res?.device) applyUpdate(res.device);
    } catch { /* upstream */ }
    finally {
      setPowerBusy(false);
    }
  };

  const togglePower = async () => {
    if (!canControl) return;
    if (offline && device.device_kind === 'ac') return;
    // Tras PIN correcto, resume llama doTogglePower (sin re-pedir PIN).
    if (!requirePinIfNeeded(device.pin_required, device.id, () => {
      void doTogglePower();
    })) return;
    await doTogglePower();
  };

  return (
    <Box
      sx={{
        ...sonosCardOuterSx,
        backgroundColor: CARD_BG,
        // Misma estética online/offline (como Shelly): sin opacidad ni “manto” gris.
        // Offline solo se indica con el badge.
        opacity: 1,
        border: device.missing_from_cloud ? '1px solid #e5a54b' : 'none',
        overflow: 'visible',
        // Scroll móvil (docs/51): igual que DeviceCard — no capturar touch en el root.
        pointerEvents: 'none',
        '& button, & .MuiIconButton-root, & .MuiButton-root, & [role="button"], & .MuiCheckbox-root, & .MuiInput-root, & [data-device-icon]': {
          pointerEvents: 'auto',
          touchAction: isRoomEditing ? 'none' : 'manipulation',
        },
      }}
    >
      {!isRoomEditing && !isEditingName && (
        <Box
          onClick={(e) => {
            if (editMode || locked) return;
            const target = e.target as HTMLElement;
            if (
              target.closest('button')
              || target.closest('.MuiIconButton-root')
              || target.closest('.MuiCheckbox-root')
              || target.closest('[data-device-icon]')
            ) {
              return;
            }
            const scrolled = (window as any).dashboardHasScrolledRef?.current;
            if (!scrolled) onOpenModal?.(device);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: '80px',
            right: '120px',
            bottom: 0,
            pointerEvents: 'auto',
            touchAction: 'pan-y',
            zIndex: 1,
            cursor: 'pointer',
          }}
        />
      )}
      <DeviceIconWithBrandChip
        brand="samsung"
        showBrandChip={showBrandChip}
        sx={{ marginRight: 1 }}
      >
        <Box data-device-icon sx={{ flexShrink: 0, touchAction: 'pan-y', pointerEvents: 'auto' }}>
          {hasArtefactoIcon ? (
            <DeviceTypeImage
              tipo="SAMSUNG"
              tipo_artefacto={tipoArtefacto}
              iconOverride={device.artefacto?.icono ?? null}
              colorOverride={device.artefacto?.color_icono ?? ACCENT}
            />
          ) : (
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%', bgcolor: '#1B1D21',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {device.device_kind === 'ac'
                ? <AcUnitIcon sx={{ fontSize: 28, color: ACCENT }} />
                : <TvIcon sx={{ fontSize: 28, color: ACCENT }} />}
            </Box>
          )}
        </Box>
      </DeviceIconWithBrandChip>

      <Box sx={{
        // flex:1 como antes del alineado del lápiz — con 0.8 el chip modo/ventilador AC se cortaba.
        // Columna derecha: power visible, o reserva en edición para el lápiz (sin achicar el contenido).
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        transform: 'translateY(-2px)',
        pr: (showPower || (editMode && !isRoomEditing)) ? '58px' : 0,
      }}>
        {/* Nombre + lápiz: misma fila que DeviceCard (Shelly) */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 0.15,
          position: 'relative',
          zIndex: isEditingName ? 100 : 'auto',
        }}>
          {isEditingName ? (
            <>
              <TextField
                value={editingNameValue}
                onChange={(e) => setEditingNameValue(e.target.value)}
                variant="standard"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                sx={{
                  flex: 1,
                  '& .MuiInput-root': { color: 'white', fontSize: 14, fontWeight: 'bold' },
                  '& .MuiInput-underline:before': { borderBottomColor: '#666' },
                  '& .MuiInput-underline:after': { borderBottomColor: '#2391FF' },
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSaveEditName?.(editingNameValue);
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onSaveEditName?.(editingNameValue);
                }}
                sx={{
                  padding: 1,
                  marginLeft: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  zIndex: 101,
                  backgroundColor: 'rgba(35, 145, 255, 0.1)',
                }}
              >
                <CheckIcon sx={{ fontSize: 18, color: '#2391FF' }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelEditName?.();
                }}
                sx={{
                  padding: 1,
                  marginLeft: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  zIndex: 101,
                  backgroundColor: 'rgba(255, 68, 68, 0.1)',
                }}
              >
                <CloseIcon sx={{ fontSize: 18, color: '#ff4444' }} />
              </IconButton>
            </>
          ) : (
            <>
              <Typography sx={{
                fontSize: 14,
                fontWeight: 'bold',
                color: '#fff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}>
                {device.nombre}
              </Typography>
              {editMode && !isRoomEditing && onStartEditName && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartEditName();
                  }}
                  sx={{
                    padding: 0.25,
                    marginLeft: 0.5,
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' },
                  }}
                >
                  <EditIcon sx={{ fontSize: 14, color: '#2391FF' }} />
                </IconButton>
              )}
            </>
          )}
        </Box>

        {device.device_kind === 'ac' ? (() => {
          const modeKey = displayHvacMode(device.hvac_mode);
          const modeTint = HVAC_TINT[modeKey] || ACCENT;
          const modeLabel = modeKey === '—'
            ? '—'
            : (['auto', 'cool', 'heat', 'fan', 'dry', 'wind'].includes(modeKey)
              ? t(`samsung.hvac.${modeKey}`)
              : modeKey);
          const fanKey = device.fan_mode ? String(device.fan_mode).toLowerCase() : null;
          const fanLabel = fanKey
            ? (['auto', 'low', 'medium', 'high'].includes(fanKey)
              ? t(`samsung.fan.${fanKey}`)
              : String(device.fan_mode))
            : null;
          const setpointUnavailable = modeKey === 'fan' || modeKey === 'wind';
          const displaySp = localSetpoint != null
            ? localSetpoint
            : (device.setpoint != null ? Math.round(Number(device.setpoint)) : null);
          const spDisabled = !canControl || offline || setpointUnavailable;
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0,
              mt: 0.1,
              minWidth: 0,
              height: 22,
            }}>
              <Box sx={{ ...VALUE_CHIP_SX, px: 0.7, minWidth: 42 }}>
                <Typography sx={{
                  fontSize: 13,
                  fontWeight: 750,
                  color: modeTint,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                }}>
                  {device.temperature != null ? Number(device.temperature).toFixed(1) : '—'}
                  <Box component="span" sx={{ fontSize: 10, fontWeight: 600, ml: 0.1 }}>°</Box>
                </Typography>
              </Box>

              <Box sx={{
                ...STEP_CHIP_SX,
                opacity: setpointUnavailable ? 0.38 : 1,
                transition: 'opacity 160ms ease',
              }}>
                <IconButton
                  size="small"
                  disabled={spDisabled}
                  onClick={(e) => run(e, 'setpoint', (displaySp ?? 22) - 1)}
                  sx={STEP_BTN_SX}
                  aria-label={t('samsung.card.setpointDown')}
                >
                  <RemoveIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <Typography sx={STEP_VALUE_SX}>
                  {setpointUnavailable
                    ? '—'
                    : (displaySp != null ? displaySp : '—')}
                  {!setpointUnavailable && (
                    <Box component="span" sx={{ fontSize: 10, fontWeight: 600, ml: 0.1 }}>°</Box>
                  )}
                </Typography>
                <IconButton
                  size="small"
                  disabled={spDisabled}
                  onClick={(e) => run(e, 'setpoint', (displaySp ?? 22) + 1)}
                  sx={STEP_BTN_SX}
                  aria-label={t('samsung.card.setpointUp')}
                >
                  <AddIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>

              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.3,
                flexShrink: 0,
                ml: 1,
                px: 0.55,
                py: 0.15,
                borderRadius: 999,
                bgcolor: `${modeTint}18`,
                border: `1px solid ${modeTint}55`,
                height: 22,
                boxSizing: 'border-box',
              }}>
                <Box sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: modeTint,
                  flexShrink: 0,
                  boxShadow: `0 0 6px ${modeTint}`,
                }} />
                <Typography sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: modeTint,
                  whiteSpace: 'nowrap',
                }}>
                  {modeLabel}{fanLabel ? ` · ${fanLabel}` : ''}
                </Typography>
              </Box>
            </Box>
          );
        })() : (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            mt: 0.1,
            minWidth: 0,
            height: 22,
          }}>
            <Box sx={{ ...LEFT_SLOT_SX, width: 32 }}>
              <IconButton
                size="small"
                disabled={!canControl}
                onClick={(e) => run(e, 'mute', !device.mute)}
                aria-label={device.mute ? t('samsung.card.unmute') : t('samsung.card.mute')}
                sx={{
                  p: 0,
                  width: 22,
                  height: 22,
                  color: device.mute ? ACCENT : '#fff',
                  transition: PRESS,
                  WebkitTapHighlightColor: 'transparent',
                  '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(255,255,255,0.08)' },
                  '&:active:not(.Mui-disabled)': { transform: 'scale(0.88)' },
                  '&.Mui-disabled': { color: '#666' },
                }}
              >
                {device.mute
                  ? <VolumeOffIcon sx={{ fontSize: 18 }} />
                  : <VolumeUpIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>

            <Box sx={STEP_CHIP_SX}>
              <IconButton
                size="small"
                disabled={!canControl}
                onClick={(e) => run(e, 'volume_down')}
                aria-label={t('samsung.card.volumeDown')}
                sx={STEP_BTN_SX}
              >
                <RemoveIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <Typography sx={{
                ...STEP_VALUE_SX,
                color: device.mute ? 'rgba(255,255,255,0.35)' : '#fff',
                transition: 'color 140ms ease',
              }}>
                {device.volume ?? '—'}
              </Typography>
              <IconButton
                size="small"
                disabled={!canControl}
                onClick={(e) => run(e, 'volume_up')}
                aria-label={t('samsung.card.volumeUp')}
                sx={STEP_BTN_SX}
              >
                <AddIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>

            {device.input_source && (
              <Typography sx={{
                ml: 1,
                fontSize: 10,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.4)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}>
                {device.input_source}
              </Typography>
            )}
          </Box>
        )}
        {artefactoNombre ? (
          <Typography sx={artifactTypeLineSx}>
            {artefactoNombre}
          </Typography>
        ) : null}
      </Box>

      {/* Badge: offline real, o "sin enlace" si ST health OFFLINE con status cacheado (TVs). */}
      {(offline || tvCloudSleepy) && !isRoomEditing && (
        <Box sx={{ position: 'absolute', right: '80px', bottom: 6, zIndex: 100 }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 'bold',
            padding: '0px 4.4px', borderRadius: '8.2px',
            textAlign: 'center', display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', whiteSpace: 'nowrap', lineHeight: 1.0,
            color: offline ? '#ff4444' : '#e5a54b',
            backgroundColor: offline ? 'rgba(255, 68, 68, 0.1)' : 'rgba(229, 165, 75, 0.12)',
            border: offline ? '1px solid #ff4444' : '1px solid #e5a54b',
          }}>
            {offline ? t('devices.offline') : t('samsung.card.noLink')}
          </Typography>
        </Box>
      )}

      {/* Copia exacta del handle de DeviceCard (incluye translateX 40px). */}
      {isRoomEditing && (
        <Box
          {...dragHandleProps}
          sx={{
            marginLeft: 0.5,
            // Shelly usa +40px; Samsung/Sonos quedaban demasiado a la derecha → -50px ⇒ -10px
            transform: 'translateX(-10px)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            cursor: 'grab',
            pointerEvents: 'auto',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicator sx={{ color: 'white', fontSize: '2.25rem', pointerEvents: 'none' }} />
        </Box>
      )}

      {/* Badges PIN/IA: Shelly-like en TV (bottom 24); en AC alineados al renglón del título */}
      {(Boolean(device.pin_required) || aiWhitelisted || aiBlacklisted) && (
        <Box
          sx={{
            position: 'absolute',
            right: '76px',
            ...(device.device_kind === 'ac'
              ? { top: 12 }
              : { bottom: 24 }),
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        >
          {Boolean(device.pin_required) && (
            <Lock
              sx={{
                fontSize: 13,
                color: '#2391FF',
              }}
            />
          )}
          {aiWhitelisted && (
            <Typography
              component="span"
              aria-label={t('deviceList.aiPolicyAllowed') as string}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.62rem',
                fontWeight: 800,
                color: '#66bb6a',
                minHeight: 14,
                lineHeight: '14px',
                letterSpacing: '-0.02em',
                userSelect: 'none',
              }}
            >
              {t('common.aiBadge')}
            </Typography>
          )}
          {aiBlacklisted && (
            <Typography
              component="span"
              aria-label={t('deviceList.aiPolicyDenied') as string}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.62rem',
                fontWeight: 800,
                color: '#ef5350',
                minHeight: 14,
                lineHeight: '14px',
                letterSpacing: '-0.02em',
                userSelect: 'none',
              }}
            >
              <Box
                component="span"
                sx={{
                  textDecoration: 'line-through',
                  textDecorationColor: '#ef5350',
                  textDecorationThickness: '1.5px',
                }}
              >
                {t('common.aiBadge')}
              </Box>
            </Typography>
          )}
        </Box>
      )}

      {showPower && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          <PowerButton
            isOn={Boolean(device.power)}
            isUnknown={offline && device.device_kind === 'ac'}
            isLoading={powerBusy}
            onClick={() => { void togglePower(); }}
            deviceId={device.id}
          />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(SamsungDeviceCard, (prev, next) => (
  prev.device.id === next.device.id
  && prev.device.power === next.device.power
  && prev.device.temperature === next.device.temperature
  && prev.device.setpoint === next.device.setpoint
  && prev.device.volume === next.device.volume
  && prev.device.mute === next.device.mute
  && prev.device.input_source === next.device.input_source
  && prev.device.online === next.device.online
  && prev.device.health_status === next.device.health_status
  && prev.device.hvac_mode === next.device.hvac_mode
  && prev.device.fan_mode === next.device.fan_mode
  && prev.device.can_control === next.device.can_control
  && prev.device.pin_required === next.device.pin_required
  && prev.device.nombre === next.device.nombre
  && prev.device.artefacto_id === next.device.artefacto_id
  && prev.device.artefacto?.nombre === next.device.artefacto?.nombre
  && prev.device.missing_from_cloud === next.device.missing_from_cloud
  && prev.interactionLocked === next.interactionLocked
  && prev.editMode === next.editMode
  && prev.isRoomEditing === next.isRoomEditing
  && prev.isEditingName === next.isEditingName
  && prev.dragHandleProps === next.dragHandleProps
  && prev.onOpenModal === next.onOpenModal
  && prev.onUpdated === next.onUpdated
  && prev.onStartEditName === next.onStartEditName
  && prev.onSaveEditName === next.onSaveEditName
  && prev.onCancelEditName === next.onCancelEditName
  && prev.showBrandChip === next.showBrandChip
));
